import { useEffect, useState, useCallback, Fragment } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { describeError } from "@/lib/api";
import { fmtEuro } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  ArrowLeft, Plus, Trash2, Pencil, Loader2, Settings2, MoreVertical, Sigma, Calculator, Link2, ChevronRight, ChevronDown, CreditCard, GripVertical,
} from "lucide-react";
import { toast } from "sonner";

const uid = () => Math.random().toString(36).slice(2, 10);
const num = (v) => {
  if (v == null || v === "") return 0;
  const n = parseFloat(String(v).replace(/\s/g, "").replace(",", "."));
  return isFinite(n) ? n : 0;
};
const TYPE_LABELS = { text: "Texte", money: "Montant €", number: "Nombre", date: "Date", select: "Liste de choix", checkbox: "Case à cocher" };
const _rowLabelJS = (row, columns) => {
  if (row.label) return row.label;
  const t = columns.find((c) => c.type === "text");
  return (t && (row.cells || {})[t.id]) || "Ligne";
};
function computeProject(p) {
  const cols = p.columns;
  const numCols = cols.filter((c) => c.type === "money" || c.type === "number");
  const payerId = p.payer_column_id, me = p.payer_me_value, amtId = p.amount_column_id, paidId = p.paid_column_id, totalId = p.total_column_id;
  const colById = Object.fromEntries(cols.map((c) => [c.id, c]));
  const payAll = (r) => (r.payments || []).reduce((a, x) => a + num(x.amount), 0);
  const rawNum = (r, cid) => {
    if (paidId && cid === paidId && (r.payments || []).length > 0) return payAll(r);
    if (amtId && cid === amtId && totalId) return num((r.cells || {})[totalId]) - payAll(r);
    return num((r.cells || {})[cid]);
  };
  const cellVal = (r, c) => {
    if (c && c.computed) {
      const { op, operands = [] } = c.computed;
      const vals = operands.map((o) => rawNum(r, o));
      return op === "sum" ? vals.reduce((a, b) => a + b, 0) : (vals.length ? vals[0] - vals.slice(1).reduce((a, b) => a + b, 0) : 0);
    }
    return rawNum(r, c.id);
  };
  const allData = p.rows.filter((r) => (r.kind || "data") === "data");
  // Valeur effective d'une cellule telle qu'AFFICHÉE sur la ligne (avec les surcharges
  // « payé » / « reste à payer » basées sur les paiements réels), pour que les lignes
  // de sous-total / total correspondent exactement à la somme des lignes affichées.
  const effVal = (r, c) => {
    if (amtId && c.id === amtId && totalId) return num((r.cells || {})[totalId]) - payAll(r);
    if (paidId && c.id === paidId && (r.payments || []).length > 0) return payAll(r);
    return cellVal(r, c);
  };
  let section = []; const out = {};
  for (const r of p.rows) {
    const k = r.kind || "data";
    if (k === "data") {
      const cells = {}; numCols.forEach((c) => { if (c.computed) cells[c.id] = cellVal(r, c); });
      if (paidId && (r.payments || []).length > 0) cells[paidId] = payAll(r);
      if (amtId && totalId) cells[amtId] = num((r.cells || {})[totalId]) - payAll(r);
      const hasPay = (r.payments || []).length > 0;
      out[r.id] = { computed: {}, cells, prov: payAll(r), hasPay };
      section.push(r);
    } else if (k === "subtotal") {
      const c = {}; numCols.forEach((col) => c[col.id] = section.reduce((a, r2) => a + effVal(r2, col), 0));
      out[r.id] = { computed: c, cells: {}, prov: section.reduce((a, r2) => a + payAll(r2), 0) }; section = [];
    } else if (k === "total") {
      const c = {}; numCols.forEach((col) => c[col.id] = allData.reduce((a, r2) => a + effVal(r2, col), 0));
      out[r.id] = { computed: c, cells: {}, prov: allData.reduce((a, r2) => a + payAll(r2), 0) };
    }
  }
  return out;
}

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [colDialog, setColDialog] = useState(null); // {mode:'add'|'edit', col}
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [expanded, setExpanded] = useState(new Set());
  const [editingCell, setEditingCell] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const toggleExpand = (rid) => setExpanded((s) => { const n = new Set(s); n.has(rid) ? n.delete(rid) : n.add(rid); return n; });
  const moveRow = (from, to) => { if (from == null || from === to) return; mutate((n) => { const [m] = n.rows.splice(from, 1); n.rows.splice(to, 0, m); }); setDragIndex(null); };

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.get(`/projects/${id}`), api.get("/categories")])
      .then(([pr, c]) => {
        setP(pr.data);
        setCats(c.data.filter((x) => x.type === "expense" && x.scope === "annual"));
      })
      .catch((e) => { toast.error(describeError(e)); navigate("/projets"); })
      .finally(() => setLoading(false));
  }, [id, navigate]);
  useEffect(load, [load]);

  const mutate = (fn) => { setP((prev) => { const next = structuredClone(prev); fn(next); return next; }); setDirty(true); };

  const save = useCallback(async (silent = false) => {
    setSaving(true);
    try {
      const r = await api.put(`/projects/${id}`, {
        name: p.name, description: p.description, category_id: p.category_id || null,
        payer_column_id: p.payer_column_id || null, payer_me_value: p.payer_me_value || "Moi",
        amount_column_id: p.amount_column_id || null, paid_column_id: p.paid_column_id || null,
        total_column_id: p.total_column_id || null,
        label_column_id: p.label_column_id || null,
        columns: p.columns, rows: p.rows,
      });
      if (!silent) { setP(r.data); toast.success("Projet enregistré — dépenses exceptionnelles synchronisées"); }
      setDirty(false);
    } catch (e) { toast.error(describeError(e)); } finally { setSaving(false); }
  }, [id, p]);

  // Sauvegarde automatique (debounce) à chaque modification.
  useEffect(() => {
    if (!dirty || !p) return;
    const t = setTimeout(() => { save(true); }, 900);
    return () => clearTimeout(t);
  }, [p, dirty, save]);

  // ---- column ops ----
  const saveColumn = (data) => {
    mutate((n) => {
      if (colDialog.mode === "add") {
        n.columns.push({ id: uid(), name: data.name, type: data.type, options: data.options, computed: data.computed });
      } else {
        const c = n.columns.find((x) => x.id === colDialog.col.id);
        c.name = data.name; c.type = data.type; c.options = data.options; c.computed = data.computed;
      }
    });
    setColDialog(null);
  };
  const deleteColumn = (cid) => mutate((n) => {
    n.columns = n.columns.filter((c) => c.id !== cid);
    n.rows.forEach((r) => { if (r.cells) delete r.cells[cid]; });
    if (n.payer_column_id === cid) n.payer_column_id = null;
    if (n.amount_column_id === cid) n.amount_column_id = null;
  });

  // ---- row ops ----
  const addRow = (kind) => mutate((n) => {
    const cells = {}; n.columns.forEach((c) => { cells[c.id] = c.type === "checkbox" ? false : ""; });
    n.rows.push({ id: uid(), kind, label: "", cells, payments: [], provision: { enabled: false, date: null, category_id: null }, planned_id: null });
  });
  const deleteRow = (rid) => mutate((n) => { n.rows = n.rows.filter((r) => r.id !== rid); });
  const setCell = (rid, cid, val) => mutate((n) => { const r = n.rows.find((x) => x.id === rid); r.cells = r.cells || {}; r.cells[cid] = val; });
  const setLabel = (rid, val) => mutate((n) => { n.rows.find((x) => x.id === rid).label = val; });

  if (loading || !p) return <div className="h-60 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const comp = computeProject(p);
  const numericTypes = ["money", "number"];
  const catName = (cid) => cats.find((c) => c.id === cid)?.name;

  const renderCell = (row, col) => {
    const val = (row.cells || {})[col.id] ?? "";
    const common = "h-8 text-sm";
    const hasPay = (comp[row.id] || {}).hasPay;
    if (col.computed || (col.id === p.paid_column_id && hasPay) || (col.id === p.amount_column_id && p.total_column_id)) {
      const v = (comp[row.id]?.cells || {})[col.id] || 0;
      return <div className="h-8 flex items-center justify-end font-num text-sm text-muted-foreground" title="Calculée automatiquement" data-testid={`cell-${row.id}-${col.id}`}>
        {col.type === "money" || col.type === "number" ? fmtEuro(v) : v}
      </div>;
    }
    if (col.type === "checkbox")
      return <div className="flex justify-center"><Checkbox checked={!!val} onCheckedChange={(v) => setCell(row.id, col.id, !!v)} data-testid={`cell-${row.id}-${col.id}`} /></div>;
    if (col.type === "select")
      return (
        <Select value={val || undefined} onValueChange={(v) => setCell(row.id, col.id, v)}>
          <SelectTrigger className={common} data-testid={`cell-${row.id}-${col.id}`}><SelectValue placeholder="—" /></SelectTrigger>
          <SelectContent>{(col.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
      );
    // Cellules texte / montant / nombre / date : lecture seule + crayon, éditables au clic.
    const cellKey = `${row.id}:${col.id}`;
    const numeric = col.type === "money" || col.type === "number";
    if (editingCell === cellKey) {
      const commit = () => setEditingCell(null);
      if (col.type === "date")
        return <Input type="date" autoFocus value={val} onChange={(e) => setCell(row.id, col.id, e.target.value)} onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") commit(); }} className={common} data-testid={`cell-${row.id}-${col.id}`} />;
      return <Input type="text" autoFocus inputMode={numeric ? "decimal" : "text"} value={val}
        onChange={(e) => setCell(row.id, col.id, e.target.value)} onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") commit(); }}
        className={`${common} ${numeric ? "text-right font-num" : ""}`} placeholder="0" data-testid={`cell-${row.id}-${col.id}`} />;
    }
    const empty = val === "" || val == null;
    const display = numeric ? (empty ? "—" : fmtEuro(num(val))) : (empty ? "—" : String(val));
    return (
      <div className={`group h-8 flex items-center gap-1 rounded-md px-2 cursor-text hover:bg-secondary/60 transition-colors ${numeric ? "justify-end" : "justify-between"}`}
        onClick={() => setEditingCell(cellKey)} data-testid={`cell-${row.id}-${col.id}`}>
        <span className={`${numeric ? "font-num" : "truncate"} ${empty ? "text-muted-foreground" : ""}`}>{display}</span>
        <Pencil className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 shrink-0" data-testid={`cell-edit-${row.id}-${col.id}`} />
      </div>
    );
  };

  return (
    <div className="space-y-5" data-testid="project-detail-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="icon" onClick={() => navigate("/projets")} data-testid="back-to-projects"><ArrowLeft className="w-5 h-5" /></Button>
          <div className="min-w-0">
            <Input value={p.name} onChange={(e) => { setP({ ...p, name: e.target.value }); setDirty(true); }}
              className="font-display font-bold text-xl border-none px-0 h-auto focus-visible:ring-0 shadow-none" data-testid="project-title-input" />
            {p.description && <p className="text-sm text-muted-foreground truncate">{p.description}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground min-w-[110px] text-right inline-flex items-center justify-end gap-1.5" data-testid="project-save-status">
            {saving ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Enregistrement…</> : dirty ? "Modifications…" : "Enregistré ✓"}
          </span>
          <Button variant="outline" onClick={() => setSettingsOpen(true)} data-testid="project-settings-button"><Settings2 className="w-4 h-4 mr-1.5" /> Réglages</Button>
        </div>
      </div>

      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-secondary/60 border-b border-border">
                {p.columns.map((col) => (
                  <th key={col.id} className="px-2 py-2 text-left font-semibold whitespace-nowrap min-w-[130px]">
                    <div className="flex items-center justify-between gap-1">
                      <span className="truncate">{col.name}</span>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="text-muted-foreground hover:text-foreground shrink-0" data-testid={`col-menu-${col.id}`}><MoreVertical className="w-3.5 h-3.5" /></button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setColDialog({ mode: "edit", col })}>Modifier la colonne</DropdownMenuItem>
                          <DropdownMenuItem className="text-destructive" onClick={() => deleteColumn(col.id)}>Supprimer</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    <div className="text-[10px] font-normal text-muted-foreground">{TYPE_LABELS[col.type]}</div>
                  </th>
                ))}
                <th className="px-2 py-2 text-right font-semibold min-w-[120px]">
                  <div className="flex items-center justify-end gap-1"><CreditCard className="w-3.5 h-3.5" /> Payé (ligne)</div>
                </th>
                <th className="px-2 py-2 w-10">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setColDialog({ mode: "add", col: null })} data-testid="add-column-button"><Plus className="w-4 h-4" /></Button>
                </th>
              </tr>
            </thead>
            <tbody>
              {p.rows.length === 0 && (
                <tr><td colSpan={p.columns.length + 2} className="px-4 py-8 text-center text-muted-foreground">Aucune ligne. Ajoutez une ligne ci-dessous.</td></tr>
              )}
              {p.rows.map((row, rowIndex) => {
                const kind = row.kind || "data";
                const isAgg = kind === "subtotal" || kind === "total";
                const rc = comp[row.id] || { computed: {}, prov: 0 };
                return (
                  <Fragment key={row.id}>
                  <tr data-testid={`row-${row.id}`}
                    onDragOver={(e) => { if (dragIndex !== null) e.preventDefault(); }}
                    onDrop={() => moveRow(dragIndex, rowIndex)}
                    className={`border-b border-border ${dragIndex === rowIndex ? "opacity-50" : ""} ${kind === "total" ? "bg-primary/10 font-semibold" : kind === "subtotal" ? "bg-secondary/40 font-medium" : "hover:bg-secondary/20"}`}>
                    {p.columns.map((col, ci) => {
                      if (isAgg) {
                        if (ci === 0)
                          return <td key={col.id} className="px-2 py-1.5">
                            <div className="flex items-center gap-1.5">
                              <span draggable onDragStart={() => setDragIndex(rowIndex)} onDragEnd={() => setDragIndex(null)} className="cursor-grab active:cursor-grabbing text-muted-foreground/40 hover:text-muted-foreground shrink-0" title="Glisser pour réordonner" data-testid={`drag-row-${row.id}`}><GripVertical className="w-4 h-4" /></span>
                              {kind === "total" ? <Sigma className="w-3.5 h-3.5 text-primary" /> : <Calculator className="w-3.5 h-3.5 text-muted-foreground" />}
                              <Input value={row.label} onChange={(e) => setLabel(row.id, e.target.value)} placeholder={kind === "total" ? "Total" : "Sous-total"}
                                className="h-8 text-sm font-semibold border-none px-0 focus-visible:ring-0 shadow-none bg-transparent" data-testid={`label-${row.id}`} />
                            </div>
                          </td>;
                        if (numericTypes.includes(col.type))
                          return <td key={col.id} className="px-2 py-1.5 text-right font-num" data-testid={`agg-${row.id}-${col.id}`}>{fmtEuro(rc.computed[col.id] || 0)}</td>;
                        return <td key={col.id} className="px-2 py-1.5" />;
                      }
                      if (ci === 0)
                        return <td key={col.id} className="px-2 py-1.5">
                          <div className="flex items-center gap-1">
                            <span draggable onDragStart={() => setDragIndex(rowIndex)} onDragEnd={() => setDragIndex(null)} className="cursor-grab active:cursor-grabbing text-muted-foreground/40 hover:text-muted-foreground shrink-0" title="Glisser pour réordonner" data-testid={`drag-row-${row.id}`}><GripVertical className="w-4 h-4" /></span>
                            <button onClick={() => toggleExpand(row.id)} className="text-muted-foreground hover:text-foreground shrink-0" data-testid={`expand-row-${row.id}`}>
                              {expanded.has(row.id) ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                            </button>
                            <div className="flex-1">{renderCell(row, col)}</div>
                          </div>
                        </td>;
                      return <td key={col.id} className="px-2 py-1.5">{renderCell(row, col)}</td>;
                    })}
                    {/* à provisionner (part Moi) — lecture seule */}
                    <td className="px-2 py-1.5 text-right">
                      {isAgg ? (
                        <span className="font-num text-sm" data-testid={`prov-amount-${row.id}`}>{fmtEuro(rc.prov)}</span>
                      ) : (
                        <button onClick={() => toggleExpand(row.id)} className="w-full inline-flex items-center justify-end gap-3 hover:opacity-80" data-testid={`prov-summary-${row.id}`}>
                          <span className="inline-flex items-center justify-end gap-1 w-9 shrink-0 text-xs text-muted-foreground">
                            {(row.payments || []).length > 0 && <><CreditCard className="w-3.5 h-3.5" />{(row.payments || []).length}</>}
                          </span>
                          <span className="font-num text-sm text-primary w-24 text-right" data-testid={`prov-amount-${row.id}`}>{fmtEuro(rc.prov)}</span>
                        </button>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      <button onClick={() => deleteRow(row.id)} className="text-muted-foreground hover:text-destructive" data-testid={`delete-row-${row.id}`}><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                  {!isAgg && expanded.has(row.id) && (
                    <tr key={`${row.id}-pay`} data-testid={`payments-panel-${row.id}`}>
                      <td colSpan={p.columns.length + 2} className="px-4 py-3 bg-secondary/30 border-b border-border">
                        <div className="flex items-center gap-2 mb-2">
                          <CreditCard className="w-4 h-4 text-primary" />
                          <span className="text-sm font-medium">Paiements de « {(row.cells || {})[p.columns[0]?.id] || _rowLabelJS(row, p.columns)} »</span>
                        </div>
                        <div className="space-y-1.5">
                          {(row.payments || []).length === 0 && <p className="text-xs text-muted-foreground">Aucune dépense rattachée. Ajoutez-en depuis l'onglet « Dépenses Mensuelles » en la reliant à ce projet et à cette ligne.</p>}
                          {(row.payments || []).map((pm) => (
                            <div key={pm.id} className="flex items-center gap-3 text-sm border-b border-border/40 py-1.5" data-testid={`payment-${row.id}-${pm.id}`}>
                              <span className="w-24 text-xs text-muted-foreground shrink-0">{(pm.date || "").slice(0, 10) || "—"}</span>
                              <span className="flex-1 truncate">{pm.vendor || "—"}</span>
                              <span className="text-xs text-muted-foreground shrink-0">{pm.payer || ""}</span>
                              <span className="font-num shrink-0 w-24 text-right">{fmtEuro(num(pm.amount))}</span>
                            </div>
                          ))}
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-2">« Déjà payé » = somme des dépenses mensuelles rattachées à cette ligne. Pour ajouter/modifier un paiement, passez par l'onglet « Dépenses Mensuelles ».</p>
                      </td>
                    </tr>
                  )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex items-center gap-2 p-3 border-t border-border bg-secondary/20">
          <Button variant="outline" size="sm" onClick={() => addRow("data")} data-testid="add-row-button"><Plus className="w-4 h-4 mr-1" /> Ligne</Button>
          <Button variant="outline" size="sm" onClick={() => addRow("subtotal")} data-testid="add-subtotal-button"><Calculator className="w-4 h-4 mr-1" /> Sous-total</Button>
          <Button variant="outline" size="sm" onClick={() => addRow("total")} data-testid="add-total-button"><Sigma className="w-4 h-4 mr-1" /> Total</Button>
        </div>
      </Card>

      <p className="text-xs text-muted-foreground">
        Chaque projet crée <strong>une seule ligne dans les Prévisions</strong> (Vue Annuelle) : <strong>Prévu</strong> = le Montant total du projet, <strong>Déjà dépensé</strong> = la somme des paiements saisis, <strong>Reste à dépenser</strong> = la différence. Mis à jour à l'enregistrement (et à l'ouverture du projet).
      </p>

      <ColumnDialog dialog={colDialog} columns={p.columns} onClose={() => setColDialog(null)} onSave={saveColumn} />
      <SettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} p={p} cats={cats}
        onChange={(patch) => { setP({ ...p, ...patch }); setDirty(true); }} />
    </div>
  );
}

function ColumnDialog({ dialog, columns, onClose, onSave }) {
  const [name, setName] = useState("");
  const [type, setType] = useState("text");
  const [optionsText, setOptionsText] = useState("");
  const [computedOn, setComputedOn] = useState(false);
  const [op, setOp] = useState("subtract");
  const [base, setBase] = useState("");
  const [operands, setOperands] = useState([]); // subtrahends (subtract) or addends (sum)
  useEffect(() => {
    if (dialog) {
      setName(dialog.col?.name || "");
      setType(dialog.col?.type || "text");
      setOptionsText((dialog.col?.options || []).join(", "));
      const cc = dialog.col?.computed;
      setComputedOn(!!cc);
      setOp(cc?.op || "subtract");
      if (cc?.op === "sum") { setBase(""); setOperands(cc.operands || []); }
      else { setBase((cc?.operands || [])[0] || ""); setOperands((cc?.operands || []).slice(1)); }
    }
  }, [dialog]);
  if (!dialog) return null;
  const isNumeric = type === "money" || type === "number";
  const selfId = dialog.col?.id;
  // colonnes numériques non calculées, différentes de la colonne courante (pas de récursion)
  const numOptions = (columns || []).filter((c) => (c.type === "money" || c.type === "number") && !c.computed && c.id !== selfId);
  const toggleOperand = (id) => setOperands((o) => o.includes(id) ? o.filter((x) => x !== id) : [...o, id]);
  const submit = () => {
    if (!name.trim()) { toast.error("Nom de colonne requis"); return; }
    let computed = null;
    if (isNumeric && computedOn) {
      if (op === "subtract") {
        if (!base) { toast.error("Choisissez la colonne de base"); return; }
        computed = { op: "subtract", operands: [base, ...operands.filter((x) => x !== base)] };
      } else {
        if (operands.length < 2) { toast.error("Choisissez au moins 2 colonnes à additionner"); return; }
        computed = { op: "sum", operands };
      }
    }
    onSave({ name: name.trim(), type, options: type === "select" ? optionsText.split(",").map((s) => s.trim()).filter(Boolean) : [], computed });
  };
  return (
    <Dialog open={!!dialog} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="column-dialog">
        <DialogHeader><DialogTitle>{dialog.mode === "add" ? "Ajouter une colonne" : "Modifier la colonne"}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-1">
          <div><Label>Nom</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="column-name-input" /></div>
          <div>
            <Label>Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="column-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {type === "select" && (
            <div><Label>Options (séparées par des virgules)</Label>
              <Input value={optionsText} onChange={(e) => setOptionsText(e.target.value)} placeholder="Moi, Ma société" data-testid="column-options-input" /></div>
          )}
          {isNumeric && (
            <div className="rounded-lg border border-border p-3 space-y-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={computedOn} onCheckedChange={(v) => setComputedOn(!!v)} data-testid="column-computed-toggle" />
                <span className="text-sm font-medium">Calculée automatiquement</span>
              </label>
              {computedOn && (
                <div className="space-y-3">
                  <div>
                    <Label>Opération</Label>
                    <Select value={op} onValueChange={setOp}>
                      <SelectTrigger data-testid="column-op-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="subtract">Soustraction (A − B − …)</SelectItem>
                        <SelectItem value="sum">Addition (A + B + …)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {numOptions.length === 0 ? (
                    <p className="text-xs text-muted-foreground">Ajoutez d'abord d'autres colonnes montant/nombre pour composer le calcul.</p>
                  ) : op === "subtract" ? (
                    <>
                      <div>
                        <Label>Colonne de base</Label>
                        <Select value={base || undefined} onValueChange={setBase}>
                          <SelectTrigger data-testid="column-base-select"><SelectValue placeholder="Choisir" /></SelectTrigger>
                          <SelectContent>{numOptions.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Soustraire</Label>
                        <div className="space-y-1.5 mt-1">
                          {numOptions.filter((c) => c.id !== base).map((c) => (
                            <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer">
                              <Checkbox checked={operands.includes(c.id)} onCheckedChange={() => toggleOperand(c.id)} data-testid={`column-operand-${c.id}`} />
                              {c.name}
                            </label>
                          ))}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div>
                      <Label>Additionner</Label>
                      <div className="space-y-1.5 mt-1">
                        {numOptions.map((c) => (
                          <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer">
                            <Checkbox checked={operands.includes(c.id)} onCheckedChange={() => toggleOperand(c.id)} data-testid={`column-operand-${c.id}`} />
                            {c.name}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter><Button onClick={submit} data-testid="column-save-button">Enregistrer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SettingsDialog({ open, onClose, p, cats, onChange }) {
  const selectCols = p.columns.filter((c) => c.type === "select");
  const numCols = p.columns.filter((c) => c.type === "money" || c.type === "number");
  const payerCol = p.columns.find((c) => c.id === p.payer_column_id);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="project-settings-dialog">
        <DialogHeader><DialogTitle>Réglages du projet</DialogTitle></DialogHeader>
        <div className="space-y-4 py-1">
          <div>
            <Label>Colonne « Qui paye »</Label>
            <Select value={p.payer_column_id || "__none__"} onValueChange={(v) => onChange({ payer_column_id: v === "__none__" ? null : v, payer_me_value: null })}>
              <SelectTrigger data-testid="settings-payer-col"><SelectValue placeholder="Aucune" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Aucune (tout compte comme « Moi »)</SelectItem>
                {selectCols.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {payerCol && (
            <div>
              <Label>Valeur qui signifie « Moi » (part personnelle)</Label>
              <Select value={p.payer_me_value || undefined} onValueChange={(v) => onChange({ payer_me_value: v })}>
                <SelectTrigger data-testid="settings-me-value"><SelectValue placeholder="Choisir" /></SelectTrigger>
                <SelectContent>{(payerCol.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Colonne « Montant total »</Label>
            <Select value={p.total_column_id || "__none__"} onValueChange={(v) => onChange({ total_column_id: v === "__none__" ? null : v })}>
              <SelectTrigger data-testid="settings-total-col"><SelectValue placeholder="Aucune" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Aucune</SelectItem>
                {numCols.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Colonne « Reste à payer » (calculée = Montant − paiements)</Label>
            <Select value={p.amount_column_id || "__none__"} onValueChange={(v) => onChange({ amount_column_id: v === "__none__" ? null : v })}>
              <SelectTrigger data-testid="settings-amount-col"><SelectValue placeholder="Aucune" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Aucune</SelectItem>
                {numCols.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Colonne « Déjà payé » (remplie par les paiements)</Label>
            <Select value={p.paid_column_id || "__none__"} onValueChange={(v) => onChange({ paid_column_id: v === "__none__" ? null : v })}>
              <SelectTrigger data-testid="settings-paid-col"><SelectValue placeholder="Aucune" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Aucune</SelectItem>
                {numCols.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Catégorie exceptionnelle par défaut</Label>
            <Select value={p.category_id || "__none__"} onValueChange={(v) => onChange({ category_id: v === "__none__" ? null : v })}>
              <SelectTrigger data-testid="settings-category"><SelectValue placeholder="Aucune" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Aucune</SelectItem>
                {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter><Button onClick={onClose} data-testid="settings-close-button">Fermer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
